import {
  CustomerClassificationQueryParams,
  ProductSetCode,
  useCustomerClassificationV2Query,
} from '@/src/api/use-customer-classification-query';
import { useSupplyPointsQuery } from '@/src/api/kapi/use-supply-points-query';
import { useAccountContext } from '@ovotech/ui-tools';
import { useProfileQueryKapi } from '@/src/api/kapi/use-profile-query';
import { mapOhsClassifySearchAddress } from '@/src/utils/map-ohs-classify-search-address';

/**
 * Queries the v2 customer classification service, which returns a status per
 * Home Services product set. A product set is treated as currently held when
 * its status is 'active' or 'recent' (mirroring the existing nav blocking).
 *
 * @returns hasBoilerInsurance - customer holds a boiler cover product (incl. legacy BC).
 * @returns hasAbs - customer holds an Annual Boiler Service product.
 * @returns isReady - the classification has been retrieved, so the booleans can be trusted.
 */
export const useCustomerClassificationV2 = () => {
  const { accountId } = useAccountContext();
  const profile = useProfileQueryKapi();
  const supplyPoints = useSupplyPointsQuery();
  const address = mapOhsClassifySearchAddress([
    ...(supplyPoints.data?.account?.accountSupplyPoints ?? []),
  ]);

  const profileReady = !!profile?.data?.customer_nextV1;
  const supplyPointsReady = !!supplyPoints.data?.account?.accountSupplyPoints;

  const enabled = !!accountId && profileReady && supplyPointsReady;

  const params: CustomerClassificationQueryParams = {
    ovoAccountId: accountId!,
    firstname: profile?.data?.customer_nextV1?.givenName,
    lastname: profile?.data?.customer_nextV1?.familyName,
    email: profile?.data?.customer_nextV1?.contactEmail,
    phoneNumber: profile?.data?.customer_nextV1?.contactPhoneNumbers[0],
    addressline1: address?.addressLine1,
    postcode: address?.postCode,
  };

  const { data } = useCustomerClassificationV2Query(params, { enabled });

  const isReady = !!data?.classification;

  const holdsProductSet = (code: ProductSetCode) =>
    !!data?.classification.some(
      productSet =>
        productSet.code === code &&
        (productSet.status === 'active' || productSet.status === 'recent'),
    );

  return {
    hasBoilerInsurance: holdsProductSet('BOILER_INSURANCE'),
    hasAbs: holdsProductSet('BOILER_SERVICE'),
    hasEpd: holdsProductSet('ELECTRICS_PLUMBING_DRAINAGE'),
    isReady,
  };
};
